import { getTotalKeywordsName } from "../../../consts";
import { formatCategoryName, formatVisits } from "../../helpers";
import type { LobEntry, SiteLobData } from "../types";
import { createLobEntry, createLegendItem } from "../utils";

export const createCategories = (
    availableLobs: string[],
    lobVisitsMap: Record<string, number>,
    selectedLobs: string[],
) =>
    availableLobs.map((lob) => ({
        id: lob,
        text: formatCategoryName(lob),
        visitsText: (lobVisitsMap[lob] || 0) > 0 ? formatVisits(lobVisitsMap[lob] || 0) : "",
        isChecked: selectedLobs.includes(lob),
        isDisabled: !selectedLobs.includes(lob) && selectedLobs.length >= 5,
    }));

export const processCompareModeData = (data: Record<string, SiteLobData> | null | undefined) => {
    if (!data) {
        return { lobEntries: [], availableLobs: [], lobVisitsMap: {} };
    }

    const lobTotals = new Map<
        string,
        { visits: number; domains: string[]; currentPeriodVisits: number; comparePeriodVisits: number }
    >();

    Object.entries(data).forEach(([domain, domainData]) => {
        const analysis = domainData.LobAnalysis;
        if (!analysis) {
            return;
        }

        Object.entries(analysis).forEach(([lob, lobData]) => {
            const { Visits, YoYChange } = lobData;

            const existing = lobTotals.get(lob);
            if (existing) {
                existing.visits += Visits;
                existing.domains.push(domain);
                existing.currentPeriodVisits += YoYChange?.CurrentPeriodVisits || 0;
                existing.comparePeriodVisits += YoYChange?.ComparePeriodVisits || 0;
            } else {
                lobTotals.set(lob, {
                    visits: Visits,
                    domains: [domain],
                    currentPeriodVisits: YoYChange?.CurrentPeriodVisits || 0,
                    comparePeriodVisits: YoYChange?.ComparePeriodVisits || 0,
                });
            }
        });
    });

    const lobEntries = Array.from(lobTotals.entries())
        .map(([lob, { visits, domains, currentPeriodVisits, comparePeriodVisits }]) =>
            createLobEntry(lob, visits, 0, domains, {
                CurrentPeriodVisits: currentPeriodVisits,
                ComparePeriodVisits: comparePeriodVisits,
            }),
        )
        .sort((a, b) => b.visits - a.visits);

    const lobVisitsMap = Object.fromEntries(Array.from(lobTotals.entries()).map(([lob, { visits }]) => [lob, visits]));

    const availableLobs = Object.entries(lobVisitsMap)
        .sort(([, a], [, b]) => b - a)
        .map(([lobName]) => lobName);

    return { lobEntries, availableLobs, lobVisitsMap };
};

export const createCompareModeLegend = (data: Record<string, SiteLobData> | null | undefined) =>
    Object.keys(data || {}).map((domain, idx) =>
        createLegendItem(domain, domain, formatVisits(data?.[domain]?.SiteVisits || 0).toString(), idx, idx < 5),
    );

export const createChartDataWithVisibility = (
    lobEntries: LobEntry[],
    visibleDomains: Set<string>,
    data: Record<string, SiteLobData> | null | undefined,
    selectedLobs: string[],
) => {
    if (!data) {
        return { allChartData: [], visibleChartData: [] };
    }

    const chartData = lobEntries
        .filter((entry) => entry.domains.some((domain) => visibleDomains.has(domain)))
        .map((entry) => {
            const trafficShareData: Record<string, number> = {};
            const domainYoYChanges: Record<string, { currentPeriodVisits: number; comparePeriodVisits: number }> = {};

            let totalVisits = 0;
            entry.domains.forEach((domain) => {
                if (visibleDomains.has(domain)) {
                    const domainData = data?.[domain]?.LobAnalysis?.[entry.lob];
                    if (domainData) {
                        totalVisits += domainData.Visits || 0;
                    }
                }
            });

            entry.domains.forEach((domain) => {
                if (visibleDomains.has(domain)) {
                    const domainData = data?.[domain]?.LobAnalysis?.[entry.lob];
                    if (domainData && totalVisits > 0) {
                        trafficShareData[domain] = domainData.Visits / totalVisits;
                        domainYoYChanges[domain] = {
                            currentPeriodVisits: domainData.YoYChange?.CurrentPeriodVisits || 0,
                            comparePeriodVisits: domainData.YoYChange?.ComparePeriodVisits || 0,
                        };
                    }
                }
            });

            return {
                label: entry.lob,
                value: entry.formattedVisits,
                trafficShare: trafficShareData,
                isTotal: entry.isTotal,
                yoyChange: entry.yoyChange,
                totalVisits: { visits: entry.visits },
                domainYoYChanges: domainYoYChanges,
            };
        });

    const totalVisits = Object.values(data).reduce((total, domainData) => total + (domainData?.SiteVisits || 0), 0);
    if (totalVisits > 0) {
        chartData.push({
            label: getTotalKeywordsName(),
            value: formatVisits(totalVisits),
            trafficShare: {},
            isTotal: true,
            yoyChange: null,
            totalVisits: { visits: totalVisits },
            domainYoYChanges: {},
        });
    }

    const filteredData = chartData.filter((item) => item.isTotal || selectedLobs.includes(item.label));
    const nonTotalItems = filteredData.filter((item) => !item.isTotal);
    const totalItems = filteredData.filter((item) => item.isTotal);

    const top5NonTotalItems = nonTotalItems
        .sort((a, b) => {
            const aVisits = a.totalVisits?.visits || 0;
            const bVisits = b.totalVisits?.visits || 0;
            return bVisits - aVisits;
        })
        .slice(0, 5);

    return {
        allChartData: chartData,
        visibleChartData: [...top5NonTotalItems, ...totalItems],
    };
};
